<template>
    <Card ref="nodeRef" class="workspaceNodeCard">
        <template #title>
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                    <Button
                        :icon="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                        text
                        rounded
                        size="small"
                        @click="toggle"
                    />
                    <i class="pi text-gray-600" :class="depth === 0 ? 'pi-book' : 'pi-folder'"/>
                    <div class="flex items-center">
                        <div v-tooltip="node.name"
                            class="font-medium" :class="{
                            'truncate max-w-36': !expanded && workspaceResult,
                            'truncate max-w-64': expanded
                        }">{{ node.name }}</div>
                        <div class="text-xs ml-1" v-if="!userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, node)">(read-only)</div>
                    </div>
                    <div class="flex gap-2 items-center"
                         v-if="expanded && userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, node)">
                        <Button
                            icon="pi pi-plus"
                            label="Folder"
                            rounded
                            text
                            severity="contrast"
                            @click="addChild('workspace')"
                        />
                        <Button
                            icon="pi pi-plus"
                            label="Graph"
                            rounded
                            text
                            @click="addChild('graph')"
                        />
                        <ToggleSwitch v-model="selectionEnabled" @click="selectedMenu = node"/>
                        <span class="font-medium text-base">Enable selection</span>
                    </div>

                </div>
                <div class="flex gap-2 items-center">
                    <div v-if="!expanded" class="flex gap-2 items-center">
                        <div v-for="(v, t) in workspaceResult" class="flex flex-col items-center"
                             v-tooltip="progressTooltip(t,v)">
                            <span v-if="v" class="text-surface-900 text-base">{{ _.startCase(t) }}</span>
                            <ProgressBar v-if="v" :value="_.round(v * 100)"
                                         :pt="progressPt(v)"
                                         class="pbar"/>
                        </div>
                    </div>
                    <span class="text-surface-900 dark:text-surface-0 font-medium mr-2 text-base">
                        {{ graphCount }} graph{{ graphCount === 1 ? '' : 's' }}
                        -
                        {{ folderCount }} folder{{ folderCount === 1 ? '' : 's' }}
                    </span>
                    <Button type="button" icon="pi pi-ellipsis-v" text rounded
                            @click.prevent.stop="evt => ctxToggle(evt, node)"/>
                    <Menu ref="menu" :popup="true" :model="menuItems"/>
                </div>
            </div>
        </template>

        <template #content>
            <WorkspaceDialog
                :key="'workspace-dialog-' + node.id"
                v-model="displayWorkspaceDialog"
                :edit-element="editNode"
                :parent="node"/>

            <CreateGraphWizard
                v-model:visible="displayAddGraph"
                :key="'create-graph-' + node.id"
                :parent="node"/>

            <WorkspacePermission
                :key="'spacePerm' + node.id"
                :workspace="node"
                v-if="selectedMenu === node && userCan(WORKSPACE_PERMISSION.MANAGE_PERMISSIONS, node)"
                v-model:visible="showWorkspacePermissions"/>

            <MoveToDialog
                :graphs="selectedChildren"
                v-if="displayMoveToDialog"
                v-model:visible="displayMoveToDialog"
                @moved="displayMoveToDialog = false; selectedChildren = []; selectAllValue = false"/>

            <Menubar
                v-if="selectionEnabled"
                :model="items"
                breakpoint="100px"
                class="!fixed bottom-4 inset-x-0 mx-auto w-fit !bg-black  rounded-xl py-2 flex gap-3 shadow-lg z-50"
            />

            <!-- using v-show so that expand can be triggered bottom up -->
            <div class="pl-1 flex flex-col gap-2" v-show="expanded">
                <TreeNodeCard
                    v-for="(child, idx) in node.children || []"
                    v-model:selected="selectedChildren"
                    :key="depth + '_' + child.id"
                    :node="child"
                    :depth="depth + 1"
                    @remove="removeChild(child)"
                    @update="emit('update')"
                    :selected-space-id="selectedSpaceId"
                    @expand="expand"
                    :last="idx === node.children.length - 1"
                    :selection-enabled="selectionEnabled"
                />
            </div>
        </template>
    </Card>
</template>

<script setup>
import {ref, computed, watch, defineAsyncComponent, onMounted, nextTick} from 'vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import TreeNodeCard from './TreeNodeCard.vue';
import WorkspaceModel from "@/src/model/WorkspaceModel";
import {GRAPH_PERMISSION, WORKSPACE_PERMISSION} from "@/src/model/Permission";
import {useConfirm} from "primevue/useconfirm";
import WorkspaceDialog from "@/src/views/repo/WorkspaceDialog.vue";

const MoveToDialog = defineAsyncComponent(() => import('../graph/MoveToDialog'));
const CreateGraphWizard = defineAsyncComponent(() => import("./CreateGraphWizard"))
const WorkspacePermission = defineAsyncComponent(() => import("./WorkspacePermission"))
import {progressTooltip, progressPt} from "@/src/utils/utils";

import {useStore} from 'vuex';
import _ from "lodash-es";
import GraphModel from "@/src/model/GraphModel";

const store = useStore();

const selectionEnabled = ref(false);
const selectedChildren = ref([]);

watch(selectionEnabled, v => {
    if (!v) {
        selectedChildren.value = [];
    }
})

const selectAllValue = ref(false);

const onSelectAllToggle = (value) => {
    if (value) {
        selectedChildren.value = props.node.children.filter(c => c instanceof GraphModel && (canEditGraph(c) || canDeleteGraph(c)));
    } else {
        selectedChildren.value = [];
    }
}


const props = defineProps({
    node: WorkspaceModel,
    depth: Number,
    depthColor: String,
    selectedSpaceId: Number
});

const emit = defineEmits(['remove', 'update', 'expand']);

const expanded = ref(false);

watch(() => props.node.children?.length, (newLen, oldLen) => {
    if (newLen > (oldLen || 0)) {
        expanded.value = true;
    }
});

onMounted(() => {
    if (props.node.id === props.selectedSpaceId) {
        expand()
    }
})

const nodeRef = ref(null);

const toggle = () => {
    expanded.value = !expanded.value;
};

const expand = () => {
    expanded.value = true
    emit('expand')
    nextTick(() => {
        // scroll to the node if expanded
        if (!nodeRef.value) {
            return
        }
        nodeRef.value.$el.scrollIntoView({behavior: 'smooth', block: 'start'});
    })
}

const editNode = ref(null);

const addChild = (type) => {
    editNode.value = null
    if (type === 'workspace') {
        displayWorkspaceDialog.value = true
    } else {
        displayAddGraph.value = true;
    }
};

const removeChild = (child) => {
    props.node.children = props.node.children.filter(c => c.key !== child.key);
    emit('update');
};

const selectedMenu = computed({
    get() {
        return store.getters["repoStore/selectedMenu"]
    },
    set(value) {
        store.commit('repoStore/setSelectedMenu', value)
    }
})
const menu = ref()

const ctxToggle = (evt, ws) => {
    menu.value.toggle(evt)
    selectedMenu.value = ws;
}

watch(selectedMenu, m => {
    if (m !== props.node && menu.value && menu.value.visible) {
        menu.value.hide()
    }
    if (m !== props.node && selectionEnabled.value) {
        selectionEnabled.value = false
        resetSelection()
    }
})

const userCan = store.getters['userStore/userCan']
const showWorkspacePermissions = ref(false)
const displayWorkspaceDialog = ref(false);
const displayAddGraph = ref(false)
const displayMoveToDialog = ref(false)

const menuItems = computed(() => {
    const ws = props.node
    let res = [
        {
            label: 'Edit Folder',
            icon: 'pi pi-pencil',
            command: editSpace,
            visible: userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, ws),
            class: 'rename-workspace'
        },
        {
            label: 'Delete Folder',
            icon: 'pi pi-trash',
            command: deleteItem,
            visible: graphCount.value === 0 && userCan(WORKSPACE_PERMISSION.DELETE_WORKSPACE, ws),
            class: 'delete-workspace'
        },
        {
            label: 'Set permissions for this folder',
            icon: 'pi pi-users',
            command: () => showWorkspacePermissions.value = true,
            visible: userCan(WORKSPACE_PERMISSION.MANAGE_PERMISSIONS, ws),
            class: 'permissions-workspace'
        },

    ].filter(item => item.visible)

    if (res.length > 0) {
        return res
    }

    return [
        {
            label: 'This folder has only view permission',
            disabled: true
        }
    ]
})

const editSpace = () => {
    editNode.value = props.node
    displayWorkspaceDialog.value = true
}

const confirm = useConfirm();
const deleteItem = (event) => {
    confirm.require({
        group: 'deleteWorkspace',
        target: event.target,
        message: `Are you sure you want to delete this folder?`,
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
            //callback to execute when user confirms the action
            store.dispatch('repoStore/deleteWorkspace', props.node)
        },
    });
}

const resetSelection = () => {
    selectedChildren.value = [];
    selectAllValue.value = false;
}

const deleteSelection = () => {
    confirm.require({
        group: 'deleteWorkspace',
        message: `Are you sure you want to delete selected graphs?`,
        icon: 'pi pi-exclamation-triangle',
        acceptProps: {rounded: true},
        acceptIcon: 'pi pi-check',
        rejectProps: {rounded: true},
        rejectIcon: 'pi pi-times',
        rejectClass: 'p-button-text',
        accept: () => {
            //callback to execute when user confirms the action
            store.dispatch('repoStore/deleteGraphs', selectedChildren.value.map(n => n.id))
            resetSelection()
        },
    });
}

const workspaceResult = computed(() => {
    const children = props.node.children.filter(c => !_.isEmpty(c.result))
    if (children.length === 0) {
        return null

    }
    const ad = _.reduce(children, ([num, den], c) => {
        return [num + c.result.adherence_ratio * c.result.n_cases, den + c.result.n_cases]
    }, [0, 0])
    const ad_ratio = ad[0] / ad[1]
    return {
        'digitalization': _.meanBy(children, 'result.digitalization'),
        'adherence_ratio': ad_ratio,
        'coverage': _.maxBy(children, 'result.coverage') ? _.maxBy(children, 'result.coverage')['result']['coverage'] : 0.00,
    }
})

const graphCount = computed(() => {
    return props.node.children.filter(c => c instanceof GraphModel).length
})

const folderCount = computed(() => {
    return props.node.children.filter(c => c instanceof WorkspaceModel).length
})

const canEditGraph = (graph) => {
    const isLocked = store.getters["graphStore/isLockedByAnotherUser"](graph)
    return !isLocked && (userCan(GRAPH_PERMISSION.EDIT_GRAPH, graph) ||
        userCan(WORKSPACE_PERMISSION.EDIT_ALL_GRAPHS, graph.parent))
}

const canDeleteGraph = (graph) => {
    const isLocked = store.getters["graphStore/isLockedByAnotherUser"](graph)
    return !isLocked && (userCan(GRAPH_PERMISSION.DELETE_GRAPH, graph) ||
        userCan(WORKSPACE_PERMISSION.DELETE_ALL_GRAPHS, graph.parent))
}

watch(expanded, v => {
    if (!v) {
        // reset selection when collapsed
        resetSelection()
        selectionEnabled.value = false
    }
    if (v && props.depth === 0) {
        // load graph results when expanded at root level
        store.dispatch('repoStore/loadGraphResults', props.node.id)
    }

})


const items = computed(() => [
    {
        label: props.node.name,
        disabled: true,
        class: 'max-w-40 floating-name',
        visible: true,
    },
    {
        label: selectAllValue.value ? 'Deselect All' : 'Select All',
        icon: selectAllValue.value ? 'pi pi-check-square' : 'pi pi-stop',
        command: () => {
            selectAllValue.value = !selectAllValue.value;
            onSelectAllToggle(selectAllValue.value);
        },
        visible: true
    },
    {
        label: `Move ${selectedChildren.value.length} graph${selectedChildren.value.length === 1 ? '' : 's'}`,
        icon: 'pi pi-folder',
        command: () => {
            displayMoveToDialog.value = true
        },
        disabled: selectedChildren.value.length === 0,
        visible: _.every(selectedChildren.value, canEditGraph)
    },
    {
        label: `Delete ${selectedChildren.value.length} graph${selectedChildren.value.length === 1 ? '' : 's'}`,
        icon: 'pi pi-trash',
        command: deleteSelection,
        disabled: selectedChildren.value.length === 0,
        visible: _.every(selectedChildren.value, canDeleteGraph)
    }
].filter(item => item.visible)
);

</script>
<style scoped>
.pbar {
    height: 0.75rem;
    width: 10rem;
}

.workspaceNodeCard :deep(.p-card-body) {
    padding-right: 0 !important;
}

.workspaceNodeCard {
    box-shadow: -4px 0 8px rgba(0, 0, 0, 0.2);
}

.workspaceNodeCard :deep(.p-menubar-item-label) {
    color: white !important;
}

.workspaceNodeCard :deep(.p-menubar-item-content:hover .p-menubar-item-label) {
    color: black !important;
}

.workspaceNodeCard :deep(.floating-name .p-menubar-item-label) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

</style>
